/**
 * useTier Hook
 *
 * React hook to get user's current tier and provide tier-based access checks.
 * This hook maps the user's Stripe subscription to their tier using tierService.
 *
 * When in client view scope (viewing mentor-created content), returns the
 * 'mentorClient' tier instead of the user's subscription tier.
 *
 * @returns {Object} Returns:
 *   - tier: Current tier object or null
 *   - tierId: Current tier ID (free, basic, pro, elite, mentorClient) or 'free'
 *   - loading: Boolean - true while checking tier
 *   - error: String|null - error message if tier check failed
 *   - hasFeature: Function - check if user has access to a feature
 *   - hasContentAccess: Function - check if user has access to content type
 *   - hasInventoryAccess: Function - check if user has access to inventory type
 *   - getLimit: Function - get limit for a limit type
 *   - isTierAtLeast: Function - check if user's tier meets minimum tier
 *   - isInClientView: Boolean - true if currently in mentor's view as a client
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { useSubscription } from './useSubscription';
import { useScope } from './useScope';
import * as tierService from '../services/tierService';

export const useTier = () => {
  const { subscription, priceId, loading: subscriptionLoading, error: subscriptionError } = useSubscription();
  const { isClientView } = useScope();
  const [baseTier, setBaseTier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Map subscription priceId to base tier (user's actual subscription tier)
  useEffect(() => {
    if (subscriptionLoading) {
      setLoading(true);
      return;
    }

    if (subscriptionError) {
      setError(subscriptionError);
      setLoading(false);
      return;
    }

    try {
      // If no subscription or priceId, user is on free tier
      if (!subscription || !priceId) {
        const freeTier = tierService.getTier('free');
        setBaseTier(freeTier);
        setLoading(false);
        setError(null);
        return;
      }

      // Map priceId to tier
      const userTier = tierService.getTierFromPriceId(priceId);

      if (userTier) {
        setBaseTier(userTier);
        setError(null);
      } else {
        // If priceId doesn't match any tier, default to free
        const freeTier = tierService.getTier('free');
        setBaseTier(freeTier);
        setError(null);
      }

      setLoading(false);
    } catch (err) {
      console.error('Error mapping tier:', err);
      setError(err.message || 'Failed to determine tier');
      // Fail secure - default to free tier
      const freeTier = tierService.getTier('free');
      setBaseTier(freeTier);
      setLoading(false);
    }
  }, [subscription, priceId, subscriptionLoading, subscriptionError]);

  // Determine effective tier based on scope
  // When in client view (viewing mentor's content), use mentorClient tier
  const tier = useMemo(() => {
    if (isClientView) {
      return tierService.getTier('mentorClient');
    }
    return baseTier;
  }, [isClientView, baseTier]);

  // Get tier ID
  const tierId = useMemo(() => {
    return tier?.id || 'free';
  }, [tier]);

  // Helper function to check feature access
  const hasFeature = useCallback((featureName) => {
    if (!tier) return false;
    return tierService.hasFeature(tier, featureName);
  }, [tier]);

  // Helper function to check content access
  const hasContentAccess = useCallback((contentType) => {
    if (!tier) return false;
    return tierService.hasContentAccess(tier, contentType);
  }, [tier]);

  // Helper function to check inventory access
  const hasInventoryAccess = useCallback((inventoryType) => {
    if (!tier) return false;
    return tierService.hasInventoryAccess(tier, inventoryType);
  }, [tier]);

  // Helper function to get limit
  const getLimit = useCallback((limitType) => {
    if (!tier) return null;
    return tierService.getLimit(tier, limitType);
  }, [tier]);

  // Helper function to check if tier meets minimum
  const isTierAtLeast = useCallback((minimumTier) => {
    if (!tier) return minimumTier === 'free';
    return tierService.isTierAtLeast(tier, minimumTier);
  }, [tier]);

  return {
    tier,
    tierId,
    loading,
    error,
    hasFeature,
    hasContentAccess,
    hasInventoryAccess,
    getLimit,
    isTierAtLeast,
    isInClientView: isClientView,
  };
};

export default useTier;

